import { css, html, LitElement } from 'lit';
import { customElement, property, queryAsync, state } from 'lit/decorators.js';
// @ts-ignore
import './components/Button';
import './file-details/FileDetailsDialog';
import type { FileDetailsDialog } from './file-details/FileDetailsDialog';
import './SelectFileDialog';
import type { SelectFileDialog } from './SelectFileDialog';
import { FileSelectedEvent, MediaFile } from './types';

// Type declaration for the Cropper
interface CropperInstance {
  destroy(): void;
  // Add other Cropper methods you plan to use
}

declare global {
  interface HTMLElement {
    cropper?: CropperInstance;
  }
}

const styles = [
  css`
    :host {
      font-family: Roboto, sans-serif;
      --media-picker-primary-color: #3f51b5;
      --media-picker-on-primary-color: #fff;
      --media-picker-spacing-vertical: 12px;
      --media-picker-spacing-horizontal: 24px;
    }

    * {
      box-sizing: border-box;
      font-family: Roboto, sans-serif;
    }

    dialog {
      position: fixed;
      display: flex;
      flex-direction: column;
      inset: 0;
      transition: all 0.5s;
      padding: 0;
      box-shadow: 0 0 2rem 0 rgba(0, 0, 0, 0.5);
      background-color: #f7f9ff;
      color: #000;
      width: 100%;
      height: 100%;
      max-width: 90%;
      max-height: 90%;
      border: none;
      transform: scale(0.9);
      border-radius: 12px;
      overflow: hidden;
    }

    dialog[open] {
      animation: 0.5s cubic-bezier(0.35, 0.25, 0, 0.72) 0s 1 normal forwards
        running show-dialog;
    }

    dialog:not([open]) {
      display: none;
      pointer-events: none;
      opacity: 0;
    }

    dialog::backdrop {
      background-color: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
    }

    dialog header {
      margin: 0;
      padding: 0 24px;
      color: var(--media-picker-on-primary-color);
      background-color: var(--media-picker-primary-color);
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 64px;
    }

    dialog header h3 {
      margin: 0;
      margin-block-start: 0;
      margin-block-end: 0;
    }

    dialog header .dialog-header-actions {
      display: flex;
      justify-content: flex-end;
      gap: 24px;
    }

    dialog .dialog-content {
      box-sizing: border-box;
      flex: 1;
      overflow: auto;
    }

    dialog .dialog-content .root {
      width: 100%;
    }

    dialog .footer {
      display: flex;
      justify-content: flex-end;
      gap: 24px;
      padding: 12px 24px;
      background-color: #f3f3f3;
      border-top: 1px solid var(--media-picker-primary-color);

      position: absolute;
      left: 0;
      bottom: 0px;
      width: 100%;
      transition: all 0.5s ease-in-out;
      z-index: 1;
      transform: translateY(100px);
    }

    dialog .footer.footer--show {
      transform: translateY(0);
    }

    .image-editor {
      height: 100%;
      width: 100%;
    }

    .image-editor cropper-canvas {
      height: 100%;
      width: 100%;
    }

    .editor-toolbar {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      position: absolute;
      bottom: 12px;
      width: 100%;
      gap: 12px;
      transition: all 0.2s ease-in-out;
      transform: translateY(100px);
    }

    .editor-toolbar--edit {
      transform: translateY(0);
    }

    .editor-toolbar .editor-toolbar__button {
      cursor: pointer;
      width: 48px;
      height: 48px;

      border-radius: 8px;
      border: 1px solid var(--media-picker-primary-color);
      color: var(--media-picker-primary-color);
      font-size: 14px;
      transition: all 0.2s ease-in-out;
    }

    @keyframes show-dialog {
      100% {
        transform: scale(1);
      }
    }

    @keyframes jump {
      0% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-8px);
      }
      100% {
        transform: translateY(0);
      }
    }

    .files {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      gap: 12px;
    }

    .files__item {
      grid-column: span 3;
    }
  `,
];

type MultiType = boolean | number;

export class MediaFileField extends LitElement {
  static readonly styles = styles;
  static readonly dialogs = {
    SELECT_FILE: '.file-select-dialog',
    SELECTED_FILE: '.file-selected-dialog',
  };

  /**
   * Allow multiple file selection, by default it is false in case of true it
   * will allow multiple file selection and number value will limit the number
   * of files that can be selected.
   */
  @property({
    // type: Boolean,
    converter: (value: string | null) => {
      if (value === 'true' || value == '') return true;
      if (value === 'false') return false;

      if (value) {
        try {
          return parseInt(value);
        } catch (error) {
          return false;
        }
      }

      return false;
    },
  })
  multi: MultiType = false;

  // ***************************************************************************
  // *** Upload drop zone customization
  // ***************************************************************************
  // TODO: move to slot
  @property({ type: String })
  title: string = 'Select image';

  @property({ type: String })
  subtitle: string = 'SVG, PNG, JPG or GIF (MAX. 800x400px)';

  @property({ type: String })
  allowedFileTypes: string = 'image/*';

  // ***************************************************************************
  // *** State
  // ***************************************************************************
  @property({ type: String })
  folderPath: string = '';

  @state()
  _fileSelected: MediaFile[] = [];

  @state()
  isLoading: boolean = false;

  @queryAsync('select-file-dialog')
  _filePickerDialog!: Promise<SelectFileDialog>;

  @queryAsync('file-details-dialog')
  _fileDetailsDialog!: Promise<FileDetailsDialog>;

  logger = {
    info: (...message: (string | number | object)[]) => console.log(...message),
    error: (...message: (string | number | object)[]) =>
      console.error(...message),
  };

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.closeDialog(MediaFileField.dialogs.SELECT_FILE);
    this.closeDialog(MediaFileField.dialogs.SELECTED_FILE);
  }

  private closeDialog(selector: string) {
    const dialog = this.shadowRoot?.querySelector(
      selector
    ) as HTMLDialogElement;
    if (dialog) {
      dialog.close();
    }
  }

  private _updateFileSelection(files: MediaFile[]) {
    this._fileSelected = files;
    this.dispatchEvent(new FileSelectedEvent(files));
  }

  private handleFileSelected(event: CustomEvent) {
    this._updateFileSelection([...this._fileSelected, ...event.detail.files]);
  }

  /**
   * Remove file
   *
   * This function removes a file from the fileSelected array
   *
   * @param file The file to remove.
   */
  private _removeFile(file: MediaFile) {
    this.logger.info('[MediaPicker Web Component:_removeFile]', file);
    this._updateFileSelection(
      this._fileSelected.filter(selectedFile => selectedFile.id !== file.id)
    );
  }

  /**
   * Get max file selected
   *
   * @returns The max file selected. If multi is a number it returns the number,
   * otherwise it returns null.
   */
  private _getMaxFileSelected() {
    if (this.multi && typeof this.multi === 'number') {
      return this.multi;
    }
  }

  getImageUrl(file: MediaFile) {
    if (Object(window).hasOwnProperty('fzGetAssetsFileUrl')) {
      // @ts-ignore
      return window.fzGetAssetsFileUrl({
        url: file.url,
      });
    }

    return file.url;
  }

  /**
   * Render file card
   *
   * @param file The file to render.
   * @returns html template of the rendered file card.
   */
  private _renderFileCard(file: MediaFile) {
    return html`
      <media-file-card
        class="files__item"
        .fileName=${file.name}
        .fileSize=${file.size}
        .fileType=${file.mime}
        .url=${file.url}
        .previewUrl=${this.getImageUrl(file)}
        .width=${file.width}
        .height=${file.height}
        .selectable=${false}
        .editable=${true}
        .removable=${true}
        @remove=${this._removeFile.bind(this, file)}
      ></media-file-card>
    `;
  }

  /**
   * Render file picker button
   *
   * @returns html template of the rendered file picker button.
   */
  private _renderFilePicker() {
    return html`
      <select-file-button
        @click=${() =>
          this._filePickerDialog.then(dialog => dialog.showModal())}
        .text=${this.title}
        .subtitle=${this.subtitle}
      >
        Select image
      </select-file-button>
    `;
  }

  /**
   * Render preview or file picker
   *
   * @returns html template of the rendered preview or file picker.
   */
  private _renderPreviewOrFilePicker() {
    if (this.multi) {
      return html`
        <div class="files">
          ${this._fileSelected.map(file => this._renderFileCard(file))}

          <div class="files__item">${this._renderFilePicker()}</div>
        </div>
      `;
    }

    if (this._fileSelected.length > 0) {
      return this._renderFileCard(this._fileSelected[0]);
    }

    return this._renderFilePicker();
  }

  public setInitialData(files: MediaFile[]) {
    this._fileSelected = files;
  }

  override render() {
    return html`
      <select-file-dialog
        .maxFileSelected=${this._getMaxFileSelected()}
        @file-selected=${this.handleFileSelected.bind(this)}
      ></select-file-dialog>
      ${this._renderPreviewOrFilePicker()}
    `;
  }
}

@customElement('select-file-button')
export class MediaPickerSelectFile extends LitElement {
  static styles = css`
    .select-file-button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 220px;
      border: 1px dashed grey;
      border-radius: 8px;
      background: #fff;
      color: #000;
      gap: 24px;
      transition: all 0.2s ease-in-out;
      cursor: pointer;
    }

    .select-file-button:hover {
      box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.2),
        0px 1px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12);
    }

    .select-file-button:hover * {
      cursor: pointer;
    }

    .select-file-button .select-file-button__label {
      align-items: center;
      gap: 8px;
      display: flex;
      flex-direction: column;
    }

    .select-file-button .select-file-button__label h3 {
      margin: 0;
    }

    .select-file-button .select-file-button__label p {
      margin: 0;
    }

    .select-file-button svg {
      width: 48px;
      height: 48px;
      transition: all 0.2s ease-in-out;
    }

    .select-file-button:hover svg {
      transform: scale(1.25);
    }
  `;

  @property({ type: String })
  text: string = 'Click to select file';

  @property({ type: String })
  subtitle: string = 'SVG, PNG, JPG or GIF (MAX. 800x400px)';

  override render() {
    return html`
      <div class="select-file-button">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          xmlns:sketch="http://www.bohemiancoding.com/sketch/ns"
          width="800px"
          height="800px"
          viewBox="0 0 32 32"
          version="1.1"
        >
          <title>image-picture</title>
          <desc>Created with Sketch Beta.</desc>
          <defs></defs>
          <g
            id="Page-1"
            stroke="none"
            stroke-width="1"
            fill="none"
            fill-rule="evenodd"
            sketch:type="MSPage"
          >
            <g
              id="Icon-Set"
              sketch:type="MSLayerGroup"
              transform="translate(-360.000000, -99.000000)"
              fill="#000000"
            >
              <path
                d="M368,109 C366.896,109 366,108.104 366,107 C366,105.896 366.896,105 368,105 C369.104,105 370,105.896 370,107 C370,108.104 369.104,109 368,109 L368,109 Z M368,103 C365.791,103 364,104.791 364,107 C364,109.209 365.791,111 368,111 C370.209,111 372,109.209 372,107 C372,104.791 370.209,103 368,103 L368,103 Z M390,116.128 L384,110 L374.059,120.111 L370,116 L362,123.337 L362,103 C362,101.896 362.896,101 364,101 L388,101 C389.104,101 390,101.896 390,103 L390,116.128 L390,116.128 Z M390,127 C390,128.104 389.104,129 388,129 L382.832,129 L375.464,121.535 L384,112.999 L390,118.999 L390,127 L390,127 Z M364,129 C362.896,129 362,128.104 362,127 L362,126.061 L369.945,118.945 L380.001,129 L364,129 L364,129 Z M388,99 L364,99 C361.791,99 360,100.791 360,103 L360,127 C360,129.209 361.791,131 364,131 L388,131 C390.209,131 392,129.209 392,127 L392,103 C392,100.791 390.209,99 388,99 L388,99 Z"
                id="image-picture"
                sketch:type="MSShapeGroup"
              ></path>
            </g>
          </g>
        </svg>

        <div class="select-file-button__label">
          <h3>${this.text}</h3>

          <p>${this.subtitle}</p>
        </div>
      </div>
    `;
  }
}
